import type Dexie from 'dexie';

/**
 * **Product migration v004 — the Touchline B data model** (B design spec §4.1, flag F-2 approved).
 *
 * One additive migration, four changes, no existing store or index touched:
 *
 *   1. `matches.purpose`, backfilled **`'teamMatch'`** for every existing row.
 *   2. New table `ourPlayers` — our own roster (My Team, scenario 2). Profile-scoped.
 *   3. New table `annotations` — free-text notes on a hypothesis (scenario 17). Match-scoped.
 *   4. New table `coachCards` — the adjustments-workflow cards (scenarios 22–32). Match-scoped.
 *
 * ── Why the backfill is `'teamMatch'`, and why that is Andrew's ruling ───────
 *
 * F-2 (spec §10, RULED 2026-08-13): every match that exists before this migration ran was an A
 * match, and every A match ran the team-facing workflow — halftime talk points for the user's own
 * team. `'advanceScout'` would retroactively declare those matches to have been scouting trips and
 * would hide the team-match workflow surfaces from their records. The value is behaviour-preserving
 * in the same sense as `v003`'s `'11v11'`: what a pre-`v004` match *did* is what `'teamMatch'`
 * *means*.
 *
 * ── The literal is spelled here on purpose ──────────────────────────────────
 *
 * `'teamMatch'` is written out rather than imported from `types/`'s `MATCH_PURPOSES` — §13.3's
 * rule, same as `v003`: a migration that named a shared constant would change what it does the day
 * that constant changed. NFR-7 is forward-only: what this wrote in August 2026 must still be what
 * it writes in 2028.
 *
 * ── Index choices (spec §4.1) ───────────────────────────────────────────────
 *
 *   ourPlayers   createdAt                 stable roster ordering (list render)
 *   annotations  matchId                   INV-8 cascade and per-match reads
 *                [matchId+hypothesisId]    the detail sheet's per-hypothesis list
 *   coachCards   &[matchId+context]        **the ≤1-card-per-(match,context) invariant, enforced
 *                                          at the DB grain** (§4.1) — a second draft for the same
 *                                          break is a ConstraintError, not a handler bug
 *                matchId                   INV-8 cascade and the match record's card reads
 *
 * No boolean is indexed (`ourPlayers.active` is filtered in memory — IndexedDB accepts no boolean
 * key, the U-1 lesson), and nothing here touches `observations`/`hypotheses`, so U-0's measured
 * index set for the tap transaction is unchanged.
 *
 * No `reconcileNeeded` flag: nothing derived changes — `purpose` is read off a match row already
 * in hand, and the new tables start empty on every device this runs on.
 */
export function v004BDataModel(db: Dexie): void {
  db.version(4)
    .stores({
      ourPlayers: 'id, createdAt',
      annotations: 'id, matchId, [matchId+hypothesisId]',
      coachCards: 'id, &[matchId+context], matchId',
    })
    .upgrade(async (tx) => {
      await tx
        .table('matches')
        .toCollection()
        .modify((row: { purpose?: string }) => {
          row.purpose = 'teamMatch';
        });
    });
}
